import { useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  FormGroup,
  LanguageVariants,
  RichText,
  Switch,
  Text
} from 'components/common/form';
import { HomePageSettingsSummaryCount } from '.';

const HomePageSettingsSummary = () => {
  const { messages, serverConfig, theme } = useReduxState();

  const { values } = useFormState();

  const achievementsVisible = !serverConfig.hideAchievementsFromUI;
  const calendarEventsVisible = !serverConfig.hideCalendarEventsFromUI;
  const coursesVisible = !serverConfig.hideCoursesFromUI;
  const pathsVisible = !serverConfig.hidePathsFromUI;

  const handleTextKeyDown = (ev) => {
    if (ev.keyCode === 13) ev.preventDefault();
  };

  return (
    <div className="summary">
      <div className="switch">
        <Switch
          field="homePageInfoEnabled"
          id="home-page-info-enabled"
          label={messages.homePageShowInfo}
        />
      </div>

      <div
        className={`preview ${
          values?.homePageInfoEnabled ? '' : 'preview-disabled'
        }`}
      >
        <FormGroup
          label={messages.homePageSettingsUserSectionInfoTitle}
          maxWidth="100%"
        >
          <Text
            field="homePageInfoTitle"
            id="home-page-info-title"
            onKeyDown={handleTextKeyDown}
          />

          <LanguageVariants field="homePageInfoTitleTranslations" type="text" />
        </FormGroup>

        <FormGroup
          label={messages.homePageSettingsUserSectionInfoText}
          maxWidth="100%"
        >
          <RichText
            field="homePageInfoText"
            id="home-page-info-text"
            maxLength={3000}
          />

          <LanguageVariants
            field="homePageInfoTextTranslations"
            type="richtext"
          />
        </FormGroup>
      </div>

      {!serverConfig.platformCompetenciesOnly && (
        <FormGroup
          label={messages.homePageSettingsNotificationsForUsers}
          maxWidth="100%"
          style={{ marginTop: 40 }}
        >
          <div className="counts">
            {(achievementsVisible || coursesVisible) && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="warning"
                  field="homePageShowExpiringCompletions"
                  icon="hourglass-start"
                  id="home-page-show-expiring-completions"
                  label={
                    messages.homePageSettingsUserSectionExpiringCompletions
                  }
                  text={messages.completionsExpiring}
                />
              </div>
            )}

            {(coursesVisible || pathsVisible) && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="warning"
                  field="homePageShowUpcomingDeadlines"
                  icon={['far', 'clock']}
                  id="home-page-show-upcoming-deadlines"
                  label={messages.homePageSettingsUserSectionTargetSchedule}
                  text={messages.commonUpcomingTargetSchedules}
                />
              </div>
            )}

            {calendarEventsVisible && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="default"
                  field="homePageShowUpcomingEvents"
                  icon={['far', 'calendar-days']}
                  id="home-page-show-upcoming-events"
                  label={messages.homePageSettingsUserSectionUpcomingEvents}
                  text={messages.eventCalendarUpcomingEvents}
                />
              </div>
            )}
          </div>
        </FormGroup>
      )}

      {!serverConfig.platformCompetenciesOnly && (
        <FormGroup
          label={messages.homePageSettingsNotificationsForAdmins}
          maxWidth="100%"
          style={{ marginTop: 40 }}
        >
          <div className="counts">
            {coursesVisible && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="default"
                  field="homePageShowUnreviewedAnswers"
                  icon="list-alt"
                  id="home-page-show-unreviewed-answers"
                  label={messages.homePageSettingsUserSectionUnreviewedAnswers}
                  text={messages.summaryUnreviewedAnswers}
                />
              </div>
            )}

            {coursesVisible && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="default"
                  field="homePageShowUnreadFeedbacks"
                  icon="bullhorn"
                  id="home-page-show-unread-feedbacks"
                  label={messages.homePageSettingsUserSectionUnreadFeedbacks}
                  text={messages.summaryUnreadFeedbacks}
                />
              </div>
            )}

            {coursesVisible && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="default"
                  field="homePageShowUnreadChatMessages"
                  icon="comments"
                  id="home-page-show-unread-chat-messages"
                  label={messages.homePageSettingsUserSectionUnreadChatMessages}
                  text={messages.courseUnreadChatMessages}
                />
              </div>
            )}

            {(achievementsVisible || calendarEventsVisible) && (
              <div className="count">
                <HomePageSettingsSummaryCount
                  category="default"
                  field="homePageShowPendingRequests"
                  icon="door-open"
                  id="home-page-show-pending-requests"
                  label={messages.homePageSettingsUserSectionPendingRequests}
                  text={messages.commonPendingRequests}
                />
              </div>
            )}
          </div>
        </FormGroup>
      )}

      <style jsx>{`
        .summary {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          color: ${theme.textColor};
          font-size: 1.5rem;
          padding: 20px;
          width: 100%;
        }
        .switch {
          padding: 5px 0;
        }
        .preview {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          margin-top: 10px;
          padding: 15px;
        }
        .preview-disabled {
          opacity: 0.8;
        }
        .info-translations {
          margin-top: 15px;
        }
        .counts {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-top: 10px;
        }
        .count {
          width: 100%;
        }
        @media (min-width: ${breakpoints.small}px) {
          .count {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .count {
            width: calc(33.333% - 13.333px);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsSummary;
